import { useContext, useMemo } from 'react';

import { useCluster } from '@cast/shared-cluster-context-data-access';
import { WorkloadDetailsContext } from '@cast/split-me/features/cluster/workload-optimization';
import { useWorkloadGpuUtilizationSummaryQuery } from '@cast/split-me/hooks';
import { toBig } from '@cast/utils';

import { useInventoryWorkloadsTimeRangeSelection } from '../../../../_hooks/useInventoryWorkloadsTimeRangeSelection';
import { isGpuDateRangeSupported } from '../_utils/isGpuDateRangeSupported';

type GpuSummaryInterval = {
  timestamp: string;
  costImpact?: {
    onDemand: number;
    spot: number;
    spotFallback: number;
  };
  memory?: {
    availableBytes: number;
    usedBytes: number;
    wastedBytes: number;
  };
  utilization?: {
    gpuPercent: number;
    memoryPercent: number;
  };
};

type GpuSummaryTotals = {
  totalCostImpact?: {
    onDemand: number;
    spot: number;
    spotFallback: number;
    total: number;
  };
  totalGpuMemoryBytes: number;
  totalUsedGpuMemoryBytes: number;
  totalWastedGpuMemoryBytes: number;
};

export const useWorkloadGpuUtilizationSummary = () => {
  const { workload } = useContext(WorkloadDetailsContext);
  const { cluster } = useCluster();
  const { getRange, timeZone, key } = useInventoryWorkloadsTimeRangeSelection();
  const range = useMemo(() => getRange(), [key]);

  const { data, isLoading, error, refetch } =
    useWorkloadGpuUtilizationSummaryQuery({
      clusterId: cluster.id,
      workloadName: workload?.name ?? '',
      workloadType: workload?.kind ?? '',
      namespace: workload?.namespace ?? '',
      startTime: range.from,
      endTime: range.to,
      useListingPrices: false,
      enabled: isGpuDateRangeSupported(range.from, range.to),
    });

  const intervals: GpuSummaryInterval[] = useMemo(
    () =>
      (data?.intervals ?? []).map((item) => ({
        timestamp: item.timestamp ?? '',
        costImpact: item.costImpact
          ? {
              onDemand: toBig(item.costImpact.onDemand).toNumber(),
              spot: toBig(item.costImpact.spot).toNumber(),
              spotFallback: toBig(item.costImpact.spotFallback).toNumber(),
            }
          : undefined,
        memory: item.memory
          ? {
              availableBytes: toBig(item.memory.availableBytes).toNumber(),
              usedBytes: toBig(item.memory.usedBytes).toNumber(),
              wastedBytes: toBig(item.memory.wastedBytes).toNumber(),
            }
          : undefined,
        utilization: item.utilization
          ? {
              gpuPercent: toBig(item.utilization.gpuPercent).toNumber(),
              memoryPercent: toBig(item.utilization.memoryPercent).toNumber(),
            }
          : undefined,
      })),
    [data]
  );

  const totals: GpuSummaryTotals = useMemo(() => {
    const totalCostImpact = data?.totalCostImpact
      ? {
          onDemand: toBig(data.totalCostImpact.onDemand).toNumber(),
          spot: toBig(data.totalCostImpact.spot).toNumber(),
          spotFallback: toBig(data.totalCostImpact.spotFallback).toNumber(),
          total: toBig(data.totalCostImpact.onDemand)
            .add(toBig(data.totalCostImpact.spot))
            .add(toBig(data.totalCostImpact.spotFallback))
            .toNumber(),
        }
      : undefined;

    return {
      totalCostImpact,
      totalGpuMemoryBytes: toBig(data?.totalGpuMemoryBytes).toNumber(),
      totalUsedGpuMemoryBytes: toBig(data?.totalUsedGpuMemoryBytes).toNumber(),
      totalWastedGpuMemoryBytes: toBig(
        data?.totalWastedGpuMemoryBytes
      ).toNumber(),
    };
  }, [data]);

  return {
    intervals,
    totals,
    isLoading,
    error: error as Error | null | undefined,
    refetch,
    timeZone,
  };
};
